import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { createLead } from '../utils/common-lead-apis';
import { AppConfig } from '../utils/AppConfig';
import { handleTrack } from '../utils/tracker';

interface UseLeadFormProps {
  formType: string;
  leadType: string;
  initialFormData: Record<string, string>;
  fieldMapping: Record<string, string>;
}

export const useLeadForm = ({
  formType,
  leadType,
  initialFormData,
  fieldMapping,
}: UseLeadFormProps) => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const fieldName = fieldMapping[id] ?? id;

    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const token = await recaptchaRef.current?.executeAsync();

      if (!token) {
        throw new Error('reCAPTCHA token generation failed');
      }

      const payload = {
        lead_data: {
          phone_number: formData.phoneNumber || formData.phone_number,
          email: formData.emailId || formData.email || 'optionalEmail@clear.in',
          name: formData.name,
          company_name: formData.companyName || formData.company_name,
          form_type: formType,
          lead_type: leadType,
          lead_source_url: window.location.href,
        },
        lead_platforms: {},
      };

      handleTrack({
        label: 'lead_form_submit',
        valueObj: {
          phone_number: formData.phoneNumber || formData.phone_number,
          email: formData.emailId || formData.email || 'optionalEmail@clear.in',
        },
      });
      await createLead(payload as Parameters<typeof createLead>[0], token);

      // Show success message
      setIsSubmitted(true);

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: AppConfig.GTM_CONVERSION_ID,
          value: 1.0,
          currency: 'INR',
        });
      }
    } catch (error) {
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    isSubmitting,
    isSubmitted,
    recaptchaRef,
    handleInputChange,
    handleSubmit,
  };
};
